/**
 * Button -- the one button treatment (P124.2 / brand P1.5).
 *
 * Variants, one per intent:
 *   primary        navy fill (the page's main CTA on LIGHT surfaces)
 *   primaryOnDark  brass fill (the main CTA on DARK/navy surfaces, e.g.
 *                  the login splash) -- the pre-Observatory primary
 *   ghost          brass outline (secondary action)
 *   quiet          text-only, ink -> brass on hover (tertiary)
 *   danger         text-only rose (destructive: unlink / delete / revoke)
 *
 * P214.3 (Observatory): primary flips brass -> navy fill on light
 * surfaces; brass moves to primaryOnDark for navy surfaces. Status
 * colours (rag.*) never appear here -- a button is an interaction, not a
 * state. Spreads native <button> props.
 */
import type { ComponentPropsWithoutRef } from "react";

export type ButtonVariant = "primary" | "primaryOnDark" | "ghost" | "quiet" | "danger";

const VARIANT: Record<ButtonVariant, string> = {
  primary:
    "rounded-lg px-6 py-[11px] text-[13px] font-semibold bg-brand-navy text-brand-cream hover:bg-brand-navy2",
  primaryOnDark:
    "rounded-md px-3 py-1.5 text-sm font-semibold bg-brand-brass text-brand-navy hover:bg-brand-brassDark",
  ghost:
    "rounded-md px-3 py-1.5 text-sm font-medium border border-brand-brass/40 bg-brand-brass/5 text-brand-brassDark hover:bg-brand-brass/10",
  quiet: "text-sm font-medium text-ink-2 hover:text-brand-brassDark",
  danger: "text-xs font-medium text-rose-700 hover:underline",
};

const DISABLED = "disabled:opacity-50 disabled:cursor-not-allowed";

interface ButtonProps extends ComponentPropsWithoutRef<"button"> {
  variant?: ButtonVariant;
  testid?: string;
}

export function Button({
  variant = "primary",
  testid,
  type = "button",
  className = "",
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      data-testid={testid}
      className={["transition", VARIANT[variant], DISABLED, className]
        .filter(Boolean)
        .join(" ")}
      {...rest}
    >
      {children}
    </button>
  );
}
